import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js';

const ChartWrapper = ({ data, mapData, getOptions }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const initChart = async () => {
            if (!data || !mapData || !canvasRef.current) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                // Dynamically import chartjs-chart-geo
                const chartGeoModule = await import('chartjs-chart-geo');

                if (!isMounted) return;

                const { ChoroplethController, ColorScale, GeoFeature, ProjectionScale, topojson } = chartGeoModule;

                // Verify all required components are available
                if (!ChoroplethController || !ColorScale || !GeoFeature || !ProjectionScale || !topojson) {
                    throw new Error('Failed to load required Chart.js geo components');
                }

                // Clean up existing chart
                if (chartRef.current) {
                    chartRef.current.destroy();
                    chartRef.current = null;
                }

                // Register Chart.js components
                Chart.register(ChoroplethController, ProjectionScale, ColorScale, GeoFeature);

                // Prepare data
                const nation = topojson.feature(mapData, mapData.objects.nation).features[0];
                const states = topojson.feature(mapData, mapData.objects.states).features;

                const options = getOptions();
                const ctx = canvasRef.current.getContext('2d');

                // Create new chart
                chartRef.current = new Chart(ctx, {
                    type: 'choropleth',
                    data: {
                        labels: states.map((d) => d.properties.name),
                        datasets: [
                            {
                                label: 'States',
                                outline: nation,
                                data: states.map((d) => ({
                                    feature: d,
                                    value: data[d.properties.name] ? data[d.properties.name] : 0
                                }))
                            }
                        ]
                    },
                    options: options
                });

                setIsLoading(false);
            } catch (err) {
                if (isMounted) {
                    // eslint-disable-next-line no-console
                    console.error('Error initializing geo chart:', err);
                    setError(err.message);
                    setIsLoading(false);
                }
            }
        };

        initChart();

        return () => {
            isMounted = false;
            if (chartRef.current) {
                chartRef.current.destroy();
                chartRef.current = null;
            }
        };
    }, [data, mapData, getOptions]);

    if (error) {
        return (
            <div style={{ padding: '20px', textAlign: 'center', color: '#red' }}>
                <p>Failed to load geo chart: {error}</p>
                <p>Please try refreshing the page.</p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>Loading chart...</p>
            </div>
        );
    }

    return <canvas ref={canvasRef} />;
};

export default ChartWrapper;
